import { useEffect, useId, useState } from 'react';
import type { ChoiceCardChromeType } from '../utils/siteBriefChoiceChrome';
import { contrastRatio, MIN_BASE_CONTRAST, normalizeHex, PALETTE_ROLES } from '../utils/siteBriefPalette';

type SiteBriefPaletteConstructorProps = {
  chrome: ChoiceCardChromeType;
  colors: string[];
  disabled: boolean;
  onChange: (colors: string[]) => void;
};

type ColorFieldProps = {
  label: string;
  value: string;
  disabled: boolean;
  onChange: (hex: string) => void;
};

const SIX_DIGIT_HEX = /^#?[0-9a-f]{6}$/i;

function ColorField({ label, value, disabled, onChange }: ColorFieldProps) {
  const inputId = useId();
  const [text, setText] = useState(value);

  useEffect(() => {
    setText(value);
  }, [value]);

  return (
    <div className="sb-palette-ctor__field">
      <label htmlFor={inputId}>{label}</label>
      <span className="sb-palette-ctor__input">
        <input
          type="color"
          aria-label={label}
          value={value.toLowerCase()}
          disabled={disabled}
          onChange={(event) => onChange(normalizeHex(event.target.value) ?? value)}
        />
        <input
          id={inputId}
          type="text"
          spellCheck={false}
          maxLength={7}
          value={text}
          disabled={disabled}
          onChange={(event) => {
            setText(event.target.value);
            // Only a complete colour is taken while typing, so a half-typed value is never turned into another one.
            if (SIX_DIGIT_HEX.test(event.target.value.trim())) {
              onChange(normalizeHex(event.target.value) ?? value);
            }
          }}
          onBlur={() => {
            const hex = normalizeHex(text);
            if (hex) {
              onChange(hex);
            } else {
              setText(value);
            }
          }}
        />
      </span>
    </div>
  );
}

// A small version of Weblium's palette settings: five colours in the same roles and groups.
export default function SiteBriefPaletteConstructor({ chrome, colors, disabled, onChange }: SiteBriefPaletteConstructorProps) {
  const set = (index: number, hex: string) => {
    const next = [...colors];
    next[index] = hex;
    onChange(next);
  };
  const field = (index: number) => (
    <ColorField
      label={chrome.roles[PALETTE_ROLES[index]]}
      value={colors[index]}
      disabled={disabled}
      onChange={(hex) => set(index, hex)}
    />
  );
  const lowContrast = contrastRatio(colors[0], colors[4]) < MIN_BASE_CONTRAST;

  return (
    <div className="sb-palette-ctor">
      <div className="sb-palette-ctor__strip" aria-hidden="true">
        {colors.map((color, index) => (
          <i key={PALETTE_ROLES[index]} style={{ backgroundColor: color }} />
        ))}
      </div>
      <fieldset>
        <legend>{chrome.groupBrand}</legend>
        <div className="sb-palette-ctor__row">{field(2)}</div>
      </fieldset>
      <fieldset>
        <legend>{chrome.groupAccents}</legend>
        <div className="sb-palette-ctor__row">
          {field(1)}
          {field(3)}
        </div>
      </fieldset>
      <fieldset>
        <legend>{chrome.groupBase}</legend>
        <div className="sb-palette-ctor__row">
          {field(0)}
          {field(4)}
        </div>
      </fieldset>
      {lowContrast && (
        <p className="sb-palette-ctor__hint" role="status">
          {chrome.contrastLow}
        </p>
      )}
    </div>
  );
}
